import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import { DataGrid } from '@mui/x-data-grid';
import SignatureCanvas from 'react-signature-canvas'

import bulkHelpers from '../../helpers/bulk'
import { sendNewSignInToApi, getOpenSignInsFromApi, signOutCarrierViaApi } from '../../helpers/carrierSignInApi';
import { formatDateTime } from './CarrierSignInUtils';
import './CarrierSignIn.css';

const CarrierSignInPage = (props) => {

    const [openSignIns, setOpenSignIns] = useState(null);
    const [carrierOptions, setCarrierOptions] = useState(null);
    const [displayedCarrierOptions, setDisplayedCarrierOptions] = useState([]);
    const [signInType, setSignInType] = useState("");
    const [displayScacInputField, setDisplayScacInputField] = useState(false);
    const [carrierScac, setCarrierScac] = useState("");
    const [driverName, setDriverName] = useState("");
    const [dotNumber, setDotNumber] = useState("");
    const [trailerNumber, setTrailerNumber] = useState("");
    const [referenceNumber, setReferenceNumber] = useState("");
    const [proNumber, setProNumber] = useState("");
    const [deliveredGoods, setDeliveredGoods] = useState("");
    const [signatureRef, setSignatureRef] = useState({});
    const [signOutConfirmationDialogOpen, setSignOutConfirmationDialogOpen] = useState(false);
    const [signOutConfirmationDialogDriverName, setSignOutConfirmationDialogDriverName] = useState("");
    const [signOutConfirmationDialogSignInId, setSignOutConfirmationDialogSignInId] = useState(0);
    const [enableSignOut, setEnableSignOut] = useState(true);


    useEffect(() => {
        props.setLoading(true);
        if (props.token) {
            getAllCarriers();
            
            if (props.warehouse) {
                getOpenSignIns();
            } else {
                props.toast.error("IMS user is not configured properly, please contact a developer");
            }
        }
    }, [props.token, props.warehouse]);

    useEffect(() => {
        if (openSignIns !== null && carrierOptions !== null) {
            props.setLoading(false);
        }
    }, [openSignIns, carrierOptions]);

    useEffect(() => {
        if (signInType) {
            const filteredCarrierOptions = carrierOptions.filter(carrier => {
                return carrier.type === signInType || !carrier.type;
            });
            setDisplayedCarrierOptions(filteredCarrierOptions);
        }
    }, [signInType]);


    const getAllCarriers = async () => {
        try {
            const carriersResponse = await bulkHelpers.getCarriers("furhaven_bulk", props.token);

            const carrierOptions = carriersResponse.map(carrier => ({
                label: carrier.carrier,
                scac: carrier.scac,
                type: carrier.type
            }));
            setCarrierOptions(carrierOptions);
        } catch (error) {
            setCarrierOptions([]);
            console.log(error.message);
            props.toast.error("Failed to retrieve carriers from IMS database. Please contact a developer");
        }
    };

    const getOpenSignIns = async () => {
        let openSignInsFromApi = [];

        try {
            openSignInsFromApi = await getOpenSignInsFromApi(props.warehouse, props.token);
        } catch (error) {
            console.log(error.message);
            props.toast.error("Failed to retrieve open sign-ins from IMS database. Please contact a developer");
        }

        const mappedSignIns = openSignInsFromApi.map(signIn => ({
            ...signIn,
            id: signIn.sign_in_id,
            sign_in_time: formatDateTime(signIn.sign_in_time)
        }));

        setOpenSignIns(mappedSignIns);
    };

    const handleCarrierChange = (event, value) => {
        if (!value) {                           // field was cleared
            setCarrierScac("");
            setDisplayScacInputField(false);
        } else if (value.scac === "") {         // carrier without a scac was selected
            setCarrierScac("");
            setDisplayScacInputField(true);
        } else {                                // carrier w/scac was selected
            setCarrierScac(value.scac);
            setDisplayScacInputField(false);
        }
    };

    const reset = () => {
        setSignInType("");
        setCarrierScac("");
        setDriverName("");
        setDotNumber("");
        setTrailerNumber("");
        setProNumber("");
        setDeliveredGoods("");
        setReferenceNumber("");
        signatureRef.clear();
        setDisplayScacInputField(false);
    };


    const submitSignIn = async () => {
        const fieldsToValidate = [
            carrierScac,
            driverName,
            dotNumber,
            trailerNumber
        ];

        if (signInType === "ftl") {
            fieldsToValidate.push(proNumber);
        } else if (signInType === "rcv") {
            fieldsToValidate.push(deliveredGoods);
        }

        if (displayScacInputField || signInType === "ftl") {
            fieldsToValidate.push(referenceNumber);
        }

        for (let field of fieldsToValidate) {
            if (!field.length) {
                props.toast.error("Please fill out all fields before submitting");
                return;
            }
        }

        if (signatureRef.isEmpty()) {
            props.toast.error("Please add signature before submitting");
            return;
        }

        const newSignIn = {
            scac: carrierScac,
            driver_name: driverName,
            dot_number: dotNumber,
            trailer_number: trailerNumber,
            warehouse: props.warehouse,
            sign_in_time: (new Date()).toISOString(),
            signature: signatureRef.toDataURL()
        };

        if (signInType === "ftl") {
            newSignIn.pro_number = proNumber;
        } else if (signInType === "rcv") {
            newSignIn.delivered_goods = deliveredGoods;
        }

        if (displayScacInputField || signInType === "ftl") {
            newSignIn.reference_number = referenceNumber;
        }
        props.setLoading(true);
        try {
            const signInResponse = await sendNewSignInToApi(newSignIn, props.token);
            props.toast.success("Sign in was successful");

            newSignIn.id = signInResponse.data.newSignInId;
            newSignIn.sign_in_time = formatDateTime(newSignIn.sign_in_time);
            const updatedSignIns = [...openSignIns, newSignIn];
            setOpenSignIns(updatedSignIns);
            reset();
        } catch (error) {
            props.toast.error("Failed to save sign-in to IMS database. Please contact a developer");
            console.log(error.message);
        }
        props.setLoading(false);
    };

    const onClickSignOutButton = (signInInfo) => {
        setSignOutConfirmationDialogDriverName(signInInfo.driver_name);
        setSignOutConfirmationDialogSignInId(signInInfo.id);
        setSignOutConfirmationDialogOpen(true);
    };

    const closeSignOutConfirmationDialog = () => {
        setSignOutConfirmationDialogOpen(false);
        setSignOutConfirmationDialogDriverName("");
        setSignOutConfirmationDialogSignInId(0);
    };

    const confirmSignOut = async () => {
        setEnableSignOut(false);
        props.setLoading(true);
        try {
            await signOutCarrierViaApi(signOutConfirmationDialogSignInId, props.token);
            props.toast.success("Sign out was successful");

            const updatedSignIns = openSignIns.filter(openSignIn => openSignIn.id !== signOutConfirmationDialogSignInId);
            setOpenSignIns(updatedSignIns);
            closeSignOutConfirmationDialog();
        } catch (error) {
            props.toast.error("Failed to sign out. Please contact a developer");
            console.log(error.message);
        } finally {
            setEnableSignOut(true);
            props.setLoading(false);
        }
    };


    const signOutButtonStyle = {
        backgroundColor: "#666666",
        color: "#FFFFFF",
        minWidth: 85
    };

    const autocompleteStyle = {
        width: "223px",
        marginBottom: 1
    };

    const textFieldStyle = {
        display: "block",
        marginBottom: 1
    };

    const textAreaStyle = {
        width: "223px",
        marginBottom: 1
    };

    const defaultColumnConfig = {
        flex: 1,
        editable: false,
        minWidth: 100,
        maxWidth: 200
    };

    const openSignInTableColumns = [{
        ...defaultColumnConfig,
        field: 'driver_name',
        headerName: "Driver name",
    }, {
        ...defaultColumnConfig,
        field: 'scac',
        headerName: "SCAC"
    }, {
        ...defaultColumnConfig,
        field: 'sign_in_time',
        headerName: "Sign-in"
    }, {
        field: "short_button",
        headerName: "Sign Out",
        renderCell: params => {
            return (
                <Button variant="contained" style={signOutButtonStyle} onClick={() => onClickSignOutButton(params.row)}>Sign Out</Button>
            );
        }
    }];

    
    return (
        <Box sx={{marginLeft: 2}} display="flex" flexDirection="column" className={props.isMobile ? "mobile-top" : "desktop-top"}>
            <h1>Carrier Sign-In</h1>

            {signInType === "" && <>
                <Button sx={{ maxWidth: 120, margin: 0.5 }} onClick={() => setSignInType("ltl")} variant="contained">LTL</Button>
                <Button sx={{ maxWidth: 120, margin: 0.5 }} onClick={() => setSignInType("ftl")} variant="contained">FTL</Button>
                <Button sx={{ maxWidth: 120, margin: 0.5 }} onClick={() => setSignInType("rcv")} variant="contained">Receiving</Button>
            </>}

            {signInType.length > 0 && <div style={{marginLeft: 0.5}}>

                <Autocomplete
                    name='company'
                    label="Company"
                    options={displayedCarrierOptions}
                    onChange={handleCarrierChange}
                    sx={autocompleteStyle}
                    renderInput={(params) => <TextField {...params} label="Carrier" />}
                >
                </Autocomplete>

                {displayScacInputField && <TextField sx={textFieldStyle} label="SCAC" value={carrierScac} onChange={(e) => setCarrierScac(e.target.value)} />}

                <TextField sx={textFieldStyle} label="Driver name" value={driverName} onChange={(e) => setDriverName(e.target.value)} />
                <TextField sx={textFieldStyle} label="DOT number" value={dotNumber} onChange={(e) => setDotNumber(e.target.value)} />
                <TextField sx={textFieldStyle} label="Trailer number" value={trailerNumber} onChange={(e) => setTrailerNumber(e.target.value)} />
                {(displayScacInputField || signInType === "ftl") && <TextField sx={textFieldStyle} label="Reference number" value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} />}

                {signInType === "ftl" && <TextField label="PRO number" value={proNumber} onChange={(e) => setProNumber(e.target.value)} />}
                {signInType === "rcv" && <TextField multiline minRows={2} sx={textAreaStyle} label="Delivered goods" value={deliveredGoods} onChange={(e) => setDeliveredGoods(e.target.value)} />}

                <InputLabel sx={{marginTop: 2, marginLeft: 1}} id="company-input">Signature</InputLabel>
                <SignatureCanvas ref={ref => setSignatureRef(ref)} maxWidth={1} minWidth={1} canvasProps={{ className: 'signature-canvas'}} />
                <br />

                <Button style={{ marginBottom: 10 }} onClick={() => signatureRef.clear()} variant="contained">Clear signature</Button>
                <br />
                <Button style={{ marginRight: 3 }} onClick={reset} variant="contained">Back</Button>
                <Button onClick={submitSignIn} variant="contained">Submit</Button>
            </div>}

            <h2 style={{ marginTop: 75 }}>Open Sign-ins</h2>
            <Box sx={{marginTop: 0}} className={props.isMobile ? "mobile-table-box" : "desktop-table-box"}>
                <DataGrid
                    style={{ width: "70%", marginLeft: "0%" }}
                    rows={openSignIns === null ? [] : openSignIns}
                    className={props.isMobile ? "mobile-table" : "desktop-table"}
                    columns={openSignInTableColumns}
                    rowsPerPageOption={[25, 50, 100]}
                    checkboxSelection={false}
                    onRowClick={event => { }}
                />
            </Box>

            {signOutConfirmationDialogOpen && <Dialog open={true} onClose={closeSignOutConfirmationDialog}>
                <DialogTitle>Confirm sign-out for {signOutConfirmationDialogDriverName}?</DialogTitle>
                <DialogActions>
                    <Button variant="contained" onClick={() => setSignOutConfirmationDialogOpen(false)}>Back</Button>
                    <Button variant="contained"
                            onClick={confirmSignOut}
                            disabled={!enableSignOut}>Confirm sign-out</Button>
                </DialogActions>
            </Dialog>}
        </Box>
    );
};

export default CarrierSignInPage;